import React, { useContext, useRef, useEffect } from 'react';
import { GoogleMap, MarkerF, PolygonF, PolylineF } from '@react-google-maps/api';
import { FormContext } from 'context/FormContext';
import { useGoogleMaps } from 'context/GoogleContext';
import * as turf from '@turf/turf'
import IconMapMarker from 'assets/images/icon_map_marker.svg';
import IconMapMarkerLast from 'assets/images/icon_map_marker_last.svg';

const MapRoofObstacleSelection = ({ selectedZone }) => {
    const { formValues, setFormValues } = useContext(FormContext);
    const { google } = useGoogleMaps();
    const polygonRefs = useRef([]);
    const polylineRef = useRef();

    const mapStyles = {
        width: '100%',
        height: '100%',
    };

    useEffect(() => {
        formValues.Roof.Obstacles.forEach((obstacle, zIndex) => {
            const polygon = polygonRefs.current[zIndex];
            if (polygon) {
                polygon.setOptions({
                    fillOpacity: zIndex === selectedZone ? 0.35 : 0.5,
                    fillColor: zIndex === selectedZone ? "#5CC244" : "black",
                });
            }
        });
    }, [selectedZone, formValues.Roof.Obstacles]);

    const updatePolygons = () => {
        formValues.Roof.Obstacles.forEach((obstacle, index) => {
            if (polygonRefs.current[index]) {
                polygonRefs.current[index].setOptions({
                    paths: formValues.Roof.Obstacles[index].Points,
                });
                const squareMeter = google.maps.geometry.spherical.computeArea(formValues.Roof.Obstacles[index].Points);
                formValues.Roof.Obstacles[index].SquareMeter = squareMeter;
            }
        });
        setFormValues({ ...formValues });
        updateSquareMeters();
    };

    const updateSquareMeters = () => {
        const newValues = { ...formValues };
        let totalSquareMeter = 0;
        newValues.Roof.Zones.forEach((zone, zindex) => {
            totalSquareMeter = zone.SquareMeter;
            newValues.Roof.Obstacles.forEach((obstacle, oindex) => {
                const zoneArea = convertGMapsPolygonToGEOJSON(zone.Points)
                const obstacleArea = convertGMapsPolygonToGEOJSON(obstacle.Points)
                if(zoneArea.length >= 3 && obstacleArea.length >= 3){
                    const zonePolygon = turf.polygon([zoneArea]);
                    const obstaclePolygon = turf.polygon([obstacleArea]);
                    const intersect = turf.intersect(zonePolygon, obstaclePolygon);
                    if(intersect){
                        totalSquareMeter = totalSquareMeter - turf.area(intersect);
                        totalSquareMeter = Math.max(0, totalSquareMeter);
                    }
                }
            });
            // console.log('Zone', zindex, zone.SquareMeter, totalSquareMeter)
            //newValues.Roof.Zones[zindex] = {...newValues.Roof.Zones[zindex], SquareMeterUsable: parseFloat((totalSquareMeter*-1).toFixed(2))};
            newValues.Roof.Zones[zindex] = {...newValues.Roof.Zones[zindex], SquareMeterUsable: parseFloat((totalSquareMeter).toFixed(2))};
        });
        setFormValues(newValues);
    };

    function convertGMapsPolygonToGEOJSON(googleMapsPolygon) {
        const coordinates = googleMapsPolygon
            .filter((latLng) => typeof latLng.lng === "number" && typeof latLng.lat === "number")
            .map(latLng => [latLng.lng, latLng.lat]);
        if (coordinates.length >= 3) {
            const first = coordinates[0];
            const last = coordinates[coordinates.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) {
                coordinates.push(first);
            }
        }
        return coordinates;
    }

    const mapOnClick = (e) => {
        const newValues = { ...formValues };
        if(!newValues.Roof.Obstacles[selectedZone]){
            newValues.Roof.Obstacles[selectedZone] = { Points: [], SquareMeter: 0 }
        }
        newValues.Roof.Obstacles[selectedZone].Points.push({
            lat: e.latLng.lat(), lng: e.latLng.lng(), t: 1
        });
        setFormValues(newValues);
        updatePolygons();
    };

    const drawPolyline = (e) => {
        if (formValues.Roof.Obstacles[selectedZone] && polylineRef.current) {
            const obstaclePoints = formValues.Roof.Obstacles[selectedZone].Points;
            if (obstaclePoints.length) {
                const newZonePoints = [obstaclePoints[obstaclePoints.length - 1], { lat: e.latLng.lat(), lng: e.latLng.lng() }];
                const distance = google.maps.geometry.spherical.computeDistanceBetween(newZonePoints[0], newZonePoints[1]);
                if (distance > 20) {
                    polylineRef.current.setPath([]);
                    return;
                }
                polylineRef.current.setPath(newZonePoints);
            }
        }
    };

    const resetPolyline = () => {
        if (polylineRef.current) {
            polylineRef.current.setPath([]);
        }
    };

    const defaultCenter = { lat: 50.14407031082436, lng: 8.579974394470517 };
    const initialCenter = formValues.Address?.LatLng && formValues.Address.LatLng.lat && formValues.Address.LatLng.lng
        ? formValues.Address.LatLng
        : defaultCenter;

    return (
        <div className="MapContainer full-height map-container-extra">
            <GoogleMap
                mapContainerStyle={mapStyles}
                center={initialCenter}
                zoom={20}
                onLoad={(map) => {
                    map.mapTypeId = google.maps.MapTypeId.SATELLITE;
                    map.setTilt(0);
                }}
                onClick={mapOnClick}
                onMouseMove={drawPolyline}
                onMouseOut={resetPolyline}
                options={{
                    disableDefaultUI: true,
                    draggable: true
                }}
            >
                {formValues.Roof.Obstacles[selectedZone]?.Points.map((point, index) => (
                    <MarkerF
                        // title={`entry(${selectedZone}): ${formValues.Roof.Obstacles[selectedZone].Points.length}:${index + 1}`}
                        title={`Punkt ${index + 1}`}
                        position={point}
                        key={`${selectedZone}-${index}`}
                        onClick={() => {
                            const newValues = { ...formValues };
                            newValues.Roof.Obstacles[selectedZone].Points.splice(index, 1);
                            setFormValues(newValues);
                            updatePolygons();
                        }}
                        draggable={true}
                        onLoad={(marker) => {
                            // can't use onDragEnd as state is not handled properly
                            google.maps.event.clearInstanceListeners(marker);
                            marker.addListener('dragend', (e) => {
                                // console.log('onDragEnd');
                                const newValues = { ...formValues };
                                newValues.Roof.Obstacles[selectedZone].Points[index] = {
                                    lat: e.latLng.lat(),
                                    lng: e.latLng.lng(),
                                    t: 2
                                };
                                setFormValues(newValues);
                                updatePolygons();
                            });
                        }}
                        // onDragEnd={(e) => {
                        //     console.log('onDragEnd');
                        //     const newValues = { ...formValues };
                        //     newValues.Roof.Obstacles[selectedZone].Points[index] = { lat: e.latLng.lat(), lng: e.latLng.lng(), t: 2};
                        //     setFormValues(newValues);
                        //     updatePolygons();
                        // }}
                        icon={{
                            url: formValues.Roof.Obstacles[selectedZone].Points.length > index + 1 ? IconMapMarker : IconMapMarkerLast,
                            anchor: new google.maps.Point(10, 10),
                            scaledSize: new google.maps.Size(20, 20),
                        }}
                        // onUnmount={(marker) => {
                        //     google.maps.event.clearInstanceListeners(marker);
                        // }}
                    />
                ))}
                {formValues.Roof.Obstacles.map((obstacle, zIndex) => {
                    // polygonF throws error if paths is empty...
                    if (obstacle.Points.length > 0) {
                        return (
                            <PolygonF
                                paths={obstacle.Points}
                                key={zIndex}
                                onLoad={(polygon) => {
                                    polygonRefs.current[zIndex] = polygon;
                                    polygon.setOptions({
                                        strokeColor: 'green',
                                        strokeOpacity: 0.7,
                                        strokeWeight: 2,
                                        fillOpacity: zIndex === selectedZone ? 0.35 : 0.5,
                                        fillColor: zIndex === selectedZone ? "#5CC244" : "black",
                                        clickable: false,
                                    });
                                }}
                            />
                        );
                    }
                    return null;
                })}

                <PolylineF
                    path={[]}
                    onLoad={(polyline) => {
                        polylineRef.current = polyline;
                        polyline.setOptions({
                            strokeColor: 'green',
                            strokeOpacity: 1,
                            strokeWeight: 2,
                            clickable: false,
                        })
                    }}
                />
            </GoogleMap>
        </div>
    );
};

export default MapRoofObstacleSelection;
